import { z } from 'zod';

/**
 * Single source of truth for configuration.
 *
 * Replaces the old `src/envConfig.js`, which returned the literal string
 * `#{REACT_APP_API_URL}#` when a variable was missing — so a misconfigured
 * deploy failed at request time with a confusing URL instead of at boot.
 * Here the same Azure DevOps token-replacement pattern is supported, but an
 * unreplaced token is normalised to '' and then validated: required values
 * fail loudly on startup.
 */

/** Azure pipelines substitute `#{NAME}#` at release time. Treat leftovers as unset. */
function raw(value: unknown): string {
  const v = typeof value === 'string' ? value.trim() : '';
  return /^#\{.*\}#$/.test(v) ? '' : v;
}

const source = {
  apiUrl: raw(import.meta.env.VITE_API_URL),
  apiToken: raw(import.meta.env.VITE_API_TOKEN),
  clientId: raw(import.meta.env.VITE_CLIENT_ID),
  hubUrl: raw(import.meta.env.VITE_HUB_URL),
  geoApiKey: raw(import.meta.env.VITE_GEO_API_KEY),
  prefix: raw(import.meta.env.VITE_PREFIX),

  imagesUrl: raw(import.meta.env.VITE_IMAGES_URL),
  specificProductUrl: raw(import.meta.env.VITE_SPECIFIC_PRODUCT_URL),
  imagesKey: raw(import.meta.env.VITE_IMAGES_KEY),
  utilityKey: raw(import.meta.env.VITE_UTILITY_KEY),
  paymentsKey: raw(import.meta.env.VITE_PAYMENTS_KEY),
  encryptionKey: raw(import.meta.env.VITE_ENCRYPTION_KEY),

  bookingApiUrl: raw(import.meta.env.VITE_BOOKING_API_URL),
  bookingApiKey: raw(import.meta.env.VITE_BOOKING_API_KEY),
  bookingEventUrl: raw(import.meta.env.VITE_BOOKING_API_EVENT_URL),
  bookingWebfrontUrl: raw(import.meta.env.VITE_BOOKING_WEBFRONT_URL),
  bookingCancellationFee: raw(import.meta.env.VITE_BOOKING_CANCELLATION_FEE),

  hasAccountRequests: raw(import.meta.env.VITE_HAS_ACCOUNT_REQUESTS),
  has3plDeliveries: raw(import.meta.env.VITE_HAS_3PL_DELIVERIES),

  adminTitle: raw(import.meta.env.VITE_ADMIN_TITLE),
  defaultLanguage: raw(import.meta.env.VITE_DEFAULT_LANGUAGE),
  timezoneOffset: raw(import.meta.env.VITE_TIMEZONE_OFFSET),
  timesheetHourlyRate: raw(import.meta.env.VITE_TIMESHEET_HOURLY_RATE),
  timesheetDeductionRate: raw(import.meta.env.VITE_TIMESHEET_DEDUCTION_RATE),
};

const schema = z.object({
  apiUrl: z.string().url('VITE_API_URL must be a valid URL'),
  apiToken: z.string(),
  clientId: z.string(),
  hubUrl: z.string(),
  geoApiKey: z.string(),
  /** Namespaces every localStorage key so two clients on one host never collide. */
  prefix: z.string().min(1, 'VITE_PREFIX is required'),

  imagesUrl: z.string(),
  /** Public storefront root. Empty for a client with no web front. */
  specificProductUrl: z.string(),
  imagesKey: z.string(),
  utilityKey: z.string(),
  paymentsKey: z.string(),
  encryptionKey: z.string(),

  bookingApiUrl: z.string(),
  bookingApiKey: z.string(),
  bookingEventUrl: z.string(),
  bookingWebfrontUrl: z.string(),
  bookingCancellationFee: z.coerce.number().default(0),

  /**
   * Does this client invite people to apply for an account?
   *
   * Parsed by hand rather than with `z.coerce.boolean()`, which reads the
   * *string* `"false"` as true — every value but the empty one would turn the
   * feature on, which is the opposite of what the variable says.
   */
  hasAccountRequests: z
    .string()
    .transform((value) => value.toLowerCase() === 'true'),

  /** Does this client dispatch through third-party delivery providers? */
  has3plDeliveries: z
    .string()
    .transform((value) => value.toLowerCase() === 'true'),

  /** The browser tab's name. `.catch` covers an unset or unreplaced variable. */
  adminTitle: z.string().min(1).catch('Grocefy Admin'),
  defaultLanguage: z.enum(['en', 'es']).catch('en'),
  timezoneOffset: z.coerce.number().default(0),
  timesheetHourlyRate: z.coerce.number().default(0),
  timesheetDeductionRate: z.coerce.number().default(0),
});

const parsed = schema.safeParse(source);

if (!parsed.success) {
  const issues = parsed.error.issues
    .map((i) => `  • ${i.path.join('.')}: ${i.message}`)
    .join('\n');
  throw new Error(`Invalid environment configuration:\n${issues}`);
}

export const env = parsed.data;

/** Namespaced localStorage keys — one place, so nothing is spelled by hand. */
export const storageKeys = {
  token: `${env.prefix}-admin-token`,
  expiration: `${env.prefix}-admin-expire`,
  profile: `${env.prefix}-admin-profile`,
  state: `${env.prefix}-admin-currentstate`,
  business: `${env.prefix}-admin-currentstore`,
  impersonation: `${env.prefix}-admin-imper`,
  language: `${env.prefix}-admin-lang`,
  sidebar: `${env.prefix}-admin-sidebar`,
  theme: `${env.prefix}-admin-theme`,
} as const;

export type Env = typeof env;
